import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { getSafetyConfig, saveSafetyConfig } from '../../../api/safetyAssessApi';
import '../Safety.css';

// 3x3 매트릭스 데이터 (빈도 × 강도)
const MATRIX_3x3 = [
  // [강도3 × 빈도1, 강도3 × 빈도2, 강도3 × 빈도3]
  [{ score: 3, label: '3점(중)' }, { score: 6, label: '6점(상)' }, { score: 9, label: '9점(상)' }],
  [{ score: 2, label: '2점(하)' }, { score: 4, label: '4점(중)' }, { score: 6, label: '6점(상)' }],
  [{ score: 1, label: '1점(하)' }, { score: 2, label: '2점(하)' }, { score: 3, label: '3점(중)' }],
];
const FREQ_3 = ['1점 (경미)', '2점 (보통)', '3점 (자주)'];
const SEVE_3 = ['3점 (중대)', '2점 (보통)', '1점 (경미)'];

// 5x5 매트릭스 데이터
const MATRIX_5x5 = [
  [{ score: 5 }, { score: 10 }, { score: 15 }, { score: 20 }, { score: 25 }],
  [{ score: 4 }, { score: 8 }, { score: 12 }, { score: 16 }, { score: 20 }],
  [{ score: 3 }, { score: 6 }, { score: 9 }, { score: 12 }, { score: 15 }],
  [{ score: 2 }, { score: 4 }, { score: 6 }, { score: 8 }, { score: 10 }],
  [{ score: 1 }, { score: 2 }, { score: 3 }, { score: 4 }, { score: 5 }],
];
const FREQ_5 = ['1(거의 없음)', '2(드물게)', '3(가끔)', '4(자주)', '5(지속적)'];
const SEVE_5 = ['5(치명적)', '4(중대)', '3(보통)', '2(경미)', '1(무시가능)'];

// 실시간 동적 렌더링을 위해 기존 하드코딩된 getGradeClass 함수들은 삭제되었습니다.
function SafetyConfig({ comCode = '' }) {
  const { token } = useContext(AuthContext);
  const [evalMethod, setEvalMethod] = useState('3x3');

  const [config3x3, setConfig3x3] = useState({
    gradeRules: [
      { min: 1, max: 2, grade: '하', action: '현재 상태 유지', color: '#10b981' },
      { min: 3, max: 4, grade: '중', action: '개선 대책 수립 필요', color: '#f59e0b' },
      { min: 6, max: 9, grade: '상', action: '즉시 개선 및 작업 중지 고려', color: '#ef4444' }
    ]
  });

  const [config5x5, setConfig5x5] = useState({
    gradeRules: [
      { min: 1, max: 4, grade: '낮음(1단계)', action: '현재 상태 유지', color: '#3b82f6' }, // 파랑 (매우 안전)
      { min: 5, max: 9, grade: '보통(2단계)', action: '개선 대책 수립', color: '#10b981' }, // 초록 (안전)
      { min: 10, max: 15, grade: '약간높음(3단계)', action: '적절한 개선조치 우선 실행', color: '#f59e0b' },
      { min: 16, max: 20, grade: '높음(4단계)', action: '신속한 개선 조치 실행', color: '#f97316' },
      { min: 21, max: 25, grade: '매우높음(5단계)', action: '즉시 작업 중지 및 개선', color: '#ef4444' }
    ]
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!token || !comCode) return;
    try {
      const data = await getSafetyConfig(token, comCode);
      if (data) {
        if (data.evalMethod) setEvalMethod(data.evalMethod);
        if (data.gradeRules) {
          if ((data.evalMethod || '3x3') === '3x3') {
            setConfig3x3({ gradeRules: data.gradeRules });
          } else {
            setConfig5x5({ gradeRules: data.gradeRules });
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, comCode]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        evalMethod,
        gradeRules: evalMethod === '3x3' ? config3x3.gradeRules : config5x5.gradeRules
      };
      await saveSafetyConfig(payload, token, comCode);
      alert(`${evalMethod} 평가 방식이 저장되었습니다.`);
    } catch (err) {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const currentRules = evalMethod === '3x3' ? config3x3.gradeRules : config5x5.gradeRules;

  const handleRuleChange = (index, field, value) => {
    const setter = evalMethod === '3x3' ? setConfig3x3 : setConfig5x5;
    const config = evalMethod === '3x3' ? config3x3 : config5x5;
    const newRules = [...config.gradeRules];
    
    // 점수 입력(min, max)에 대한 방어 로직
    if (field === 'min' || field === 'max') {
      let num = Number(value);
      const maxLimit = evalMethod === '3x3' ? 9 : 25;
      
      // 1. 절대 한계치 방어
      if (num < 1) num = 1;
      if (num > maxLimit) num = maxLimit;
      
      newRules[index] = { ...newRules[index], [field]: num };
      
      // 2. 모순(역전) 방지
      if (field === 'min' && num > newRules[index].max) {
        newRules[index].max = num;
      }
      if (field === 'max' && num < newRules[index].min) {
        newRules[index].min = num;
      }
    } else {
      newRules[index] = { ...newRules[index], [field]: value };
    }
    
    setter({ ...config, gradeRules: newRules });
  };

  if (loading) {
    return (
      <div className="sf-page">
        <div className="sf-loading">
          <div className="sf-spinner"></div>
          <span className="sf-loading-text">설정을 불러오는 중입니다...</span>
        </div>
      </div>
    );
  }

  const matrix = evalMethod === '3x3' ? MATRIX_3x3 : MATRIX_5x5;
  const freqLabels = evalMethod === '3x3' ? FREQ_3 : FREQ_5;
  const seveLabels = evalMethod === '3x3' ? SEVE_3 : SEVE_5;

  // 우측 설정값(currentRules)을 실시간으로 감지하여 좌측 표의 색상을 결정하는 동적 함수
  const getDynamicCellStyle = (score) => {
    const rules = evalMethod === '3x3' ? config3x3.gradeRules : config5x5.gradeRules;
    const matchedRule = rules.find(r => score >= parseInt(r.min) && score <= parseInt(r.max));

    if (matchedRule) {
      return { backgroundColor: matchedRule.color, color: '#ffffff' };
    }
    return { backgroundColor: '#e2e8f0', color: '#64748b' }; // 매칭 실패 시 기본 회색
  };
  return (
    <div style={{ animation: 'fadeIn 0.2s ease-in-out' }}>

      {/* 평가 방식 토글 바 */}
      <div className="sf-toggle-bar">
        <span className="sf-toggle-label">평가 기법 선택:</span>
        <div className="sf-toggle-group">
          <button
            className={`sf-toggle-btn ${evalMethod === '3x3' ? 'active' : ''}`}
            onClick={() => setEvalMethod('3x3')}
          >
            3×3 행렬법 (빈도3 × 강도3 = 최대 9점)
          </button>
          <button
            className={`sf-toggle-btn ${evalMethod === '5x5' ? 'active' : ''}`}
            onClick={() => setEvalMethod('5x5')}
          >
            5×5 행렬법 (빈도5 × 강도5 = 최대 25점 / KRAS)
          </button>
        </div>
        <div className="sf-toggle-info d-flex align-items-center gap-2">
          <span className="sf-badge sf-badge-primary">
            현재: <strong>{evalMethod}</strong> 방식
          </span>
          <button
            type="button"
            className="sf-btn sf-btn-primary"
            onClick={handleSave}
            disabled={saving}
            style={{ borderRadius: '6px', padding: '0 16px', height: '36px', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}
          >
            {saving ? (
              <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> 저장 중...</>
            ) : (
              <><i className="bi bi-save"></i> 설정 저장</>
            )}
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* 좌측: 매트릭스 비주얼 맵 */}
        <div className="col-lg-6">
          <div className="sf-card" style={{ height: '100%' }}>
            <div className="sf-card-header sf-gradient-accent">
              <h5 className="sf-card-header-title">
                <i className="bi bi-grid-3x3-gap"></i>
                {evalMethod} 매트릭스 비주얼 맵
              </h5>
              <span className="sf-badge sf-badge-white">실시간 미리보기</span>
            </div>
            <div className="sf-card-body" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div>
                <table className="sf-matrix" style={{ tableLayout: 'fixed', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: evalMethod === '3x3' ? '22%' : '16%' }}>강도 ╲ 빈도</th>
                      {freqLabels.map((f, i) => <th key={i}>{f}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.map((row, ri) => (
                      <tr key={ri}>
                        <th>{seveLabels[ri]}</th>
                        {row.map((cell, ci) => (
                          <td key={ci}>
                            <div className="sf-matrix-cell" style={getDynamicCellStyle(cell.score)}>
                              {cell.label || `${cell.score}점`}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '16px', padding: '14px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: '0.9rem', color: '#475569', fontWeight: 500, textAlign: 'center' }}>
                  <span style={{ fontWeight: 800, color: '#1e293b' }}>위험성 점수</span> =
                  <span style={{ color: '#3b82f6', fontWeight: 800, marginLeft: '6px' }}>빈도</span> ×
                  <span style={{ color: '#ef4444', fontWeight: 800, marginLeft: '4px' }}>강도</span>
                  <span style={{ marginLeft: '10px', color: '#64748b', fontSize: '0.85rem' }}>
                    ({evalMethod === '3x3' ? '최소 1점 ~ 최대 9점' : '최소 1점 ~ 최대 25점 / KRAS 표준'})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 우측: 등급별 조치 세부 설정 */}
        <div className="col-lg-6">
          <div className="sf-card" style={{ height: '100%' }}>
            <div className="sf-card-header sf-gradient-dark">
              <h5 className="sf-card-header-title">
                <i className="bi bi-sliders"></i>
                [{evalMethod}] 등급 기준 및 조치 설정
              </h5>
              <span className="sf-badge sf-badge-white">{currentRules.length}개 등급</span>
            </div>
            <div className="sf-card-body">
              <table className="sf-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>색상</th>
                    <th style={{ width: '100px' }}>등급</th>
                    <th style={{ width: '130px' }}>점수 범위</th>
                    <th>관리 및 개선 조치 기준</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRules.map((rule, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="color"
                          className="sf-color-swatch"
                          value={rule.color}
                          onChange={(e) => handleRuleChange(idx, 'color', e.target.value)}
                          title="클릭하여 색상 변경"
                          style={{ border: `3px solid ${rule.color}`, padding: 0 }}
                        />
                      </td>
                      <td>
                        <span
                          className="sf-badge"
                          style={{ background: `${rule.color}18`, color: rule.color, fontWeight: 800, whiteSpace: 'nowrap' }}
                        >
                          {rule.grade}
                        </span>
                      </td>
                      <td>
                        <div className="sf-score-input">
                          <input
                            type="number"
                            min="1"
                            max={evalMethod === '3x3' ? 9 : 25}
                            value={rule.min}
                            onChange={(e) => handleRuleChange(idx, 'min', e.target.value)}
                          />
                          <span className="sf-score-sep">~</span>
                          <input
                            type="number"
                            min="1"
                            max={evalMethod === '3x3' ? 9 : 25}
                            value={rule.max}
                            onChange={(e) => handleRuleChange(idx, 'max', e.target.value)}
                          />
                          <span style={{ color: 'var(--sf-text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>점</span>
                        </div>
                      </td>
                      <td>
                        <input
                          className="sf-input"
                          type="text"
                          value={rule.action}
                          onChange={(e) => handleRuleChange(idx, 'action', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SafetyConfig;
